import {
  type RowData,
  type RowSelectionState,
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
} from "@tanstack/react-table";
import { useMemo } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

declare module "@tanstack/react-table" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    /** Zusätzliche Klassen für Kopf- und Datenzelle (z. B. sticky Aktionsspalte). */
    className?: string;
  }
}

// Klicks auf Bedienelemente in einer Zeile sollen nicht die Zeile öffnen.
const INTERACTIVE =
  "a,button,input,select,textarea,label,[role=checkbox],[role=combobox],[role=menuitem],[data-row-ignore]";

/**
 * Dichte Tabelle nach dem shadcn-„Data Table“-Muster (TanStack Table v8).
 * Sortierung/Pagination liegen beim Aufrufer (serverseitig), deshalb nur Core-Row-Model.
 * Mit `selection` erscheint vorne eine Checkbox-Spalte (für Bulk-Aktionen).
 */
export function DataTable<TData>({
  columns,
  data,
  getRowId,
  onRowClick,
  loading,
  skeletonRows = 8,
  empty,
  selection,
  onSelectionChange,
  rowLabel,
}: {
  columns: ColumnDef<TData, unknown>[];
  data: TData[];
  getRowId?: (row: TData) => string;
  onRowClick?: (row: TData) => void;
  loading?: boolean;
  skeletonRows?: number;
  empty?: React.ReactNode;
  selection?: RowSelectionState;
  onSelectionChange?: (next: RowSelectionState) => void;
  /** Beschriftung der Zeilen-Checkbox für Screenreader. */
  rowLabel?: (row: TData) => string;
}) {
  const selectable = selection !== undefined && onSelectionChange !== undefined;
  const allColumns = useMemo<ColumnDef<TData, unknown>[]>(
    () =>
      selectable
        ? [
            {
              id: "__select",
              meta: { className: "w-9 pr-0" },
              header: ({ table }) => (
                <Checkbox
                  checked={
                    table.getIsAllRowsSelected()
                      ? true
                      : table.getIsSomeRowsSelected()
                        ? "indeterminate"
                        : false
                  }
                  onCheckedChange={(v) =>
                    table.toggleAllRowsSelected(v === true)
                  }
                  aria-label="Alle auf dieser Seite auswählen"
                />
              ),
              cell: ({ row }) => (
                <Checkbox
                  checked={row.getIsSelected()}
                  onCheckedChange={(v) => row.toggleSelected(v === true)}
                  aria-label={
                    rowLabel
                      ? `${rowLabel(row.original)} auswählen`
                      : "Zeile auswählen"
                  }
                />
              ),
            },
            ...columns,
          ]
        : columns,
    [selectable, columns, rowLabel],
  );

  const table = useReactTable({
    data,
    columns: allColumns,
    getRowId,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: selectable,
    state: selectable ? { rowSelection: selection } : undefined,
    onRowSelectionChange: selectable
      ? (updater) =>
          onSelectionChange(
            typeof updater === "function" ? updater(selection) : updater,
          )
      : undefined,
  });

  return (
    <Table>
      <TableHeader>
        {table.getHeaderGroups().map((hg) => (
          <TableRow key={hg.id} className="hover:bg-transparent">
            {hg.headers.map((header) => (
              <TableHead
                key={header.id}
                className={header.column.columnDef.meta?.className}
              >
                {header.isPlaceholder
                  ? null
                  : flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {loading && data.length === 0 ? (
          Array.from({ length: skeletonRows }, (_, i) => (
            <TableRow key={i} className="hover:bg-transparent">
              {allColumns.map((_, j) => (
                <TableCell key={j}>
                  <Skeleton className="h-4 w-full max-w-32" />
                </TableCell>
              ))}
            </TableRow>
          ))
        ) : table.getRowModel().rows.length === 0 ? (
          <TableRow className="hover:bg-transparent">
            <TableCell
              colSpan={allColumns.length}
              className="p-0 whitespace-normal"
            >
              {empty}
            </TableCell>
          </TableRow>
        ) : (
          table.getRowModel().rows.map((row) => (
            <TableRow
              key={row.id}
              data-state={row.getIsSelected() ? "selected" : undefined}
              className={onRowClick ? "cursor-pointer" : undefined}
              onClick={
                onRowClick
                  ? (e) => {
                      const target = e.target as HTMLElement;
                      // Klicks aus Portalen (Select-Optionen, Menüs) blubbern in React
                      // bis hierher, liegen aber nicht im DOM der Zeile.
                      if (!e.currentTarget.contains(target)) return;
                      if (target.closest(INTERACTIVE)) return;
                      onRowClick(row.original);
                    }
                  : undefined
              }
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell
                  key={cell.id}
                  className={cell.column.columnDef.meta?.className}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
}
